<template>
    <div class="main">
        <c-panel v-if="loading">
            <div class="loading-form" style="min-height: 220px;">
                <img src="/public/img/loading-bar.gif" />
            </div>
        </c-panel>

        <c-panel v-else title="Calendário Escolar" titleIcon="far fa-calendar-alt">
            <el-form label-position="top">
                <el-row :gutter="10">
                    <el-col :xs="24" :sm="10" :md="8">
                        <el-form-item label="Período letivo">
                            <el-select v-model="periodoId" filterable placeholder="Selecione" @change="onPeriodoChange">
                                <el-option v-for="(p, i) in periodosList" :key="i" :label="'Período Letivo ' + p.nome" :value="p.id"></el-option>
                            </el-select>
                        </el-form-item>
                    </el-col>

                    <el-col :xs="24" :sm="14" :md="16">
                        <div class="u-text-right" style="margin-top: 28px;">
                            <el-button type="success" :disabled="!periodoYear" @click="printCalendario()">
                                IMPRIMIR
                            </el-button>
                            <el-button type="primary" :disabled="selectedDates.length < 1" @click="openManage()">
                                GERENCIAR DATAS ({{ selectedDates.length }})
                            </el-button>
                            <el-button type="danger" :disabled="selectedDates.length < 1" @click="clearSelection()">
                                LIMPAR SELEÇÃO
                            </el-button>
                        </div>
                    </el-col>
                </el-row>
            </el-form>

            <div v-if="!periodoYear" class="u-mt-10">
                <div class="alert alert-warning">
                    Selecione um período letivo para visualizar o calendário.
                </div>
            </div>

            <div v-else class="u-mt-10">
                <div v-if="isSMED && lockedGlobal" class="alert alert-danger">
                    <b>Calendário geral bloqueado.</b> Uma instituição já personalizou o calendário deste período letivo; a SMED não pode mais alterá-lo.
                </div>

                <el-row :gutter="10" class="u-mb-10">
                    <el-col :xs="24" :sm="12" :md="8">
                        <div class="metric-box">
                            <div class="metric-label">Dias letivos (marcados)</div>
                            <div class="metric-value">{{ metrics.qtdDiasLetivos }}</div>
                        </div>
                    </el-col>
                    <el-col :xs="24" :sm="12" :md="8">
                        <div class="metric-box">
                            <div class="metric-label">Dias letivos percorridos</div>
                            <div class="metric-value">{{ metrics.diasLetivosPercorridos }}</div>
                        </div>
                    </el-col>
                    <el-col :xs="24" :sm="12" :md="8">
                        <div class="metric-box">
                            <div class="metric-label">Semanas letivas percorridas</div>
                            <div class="metric-value">{{ metrics.semanasLetivasPercorridas }}</div>
                        </div>
                    </el-col>
                </el-row>

                <div class="legend">
                    <div class="legend-item" v-for="(t, i) in tipos" :key="i">
                        <span class="legend-color" :style="{ backgroundColor: t.color }"></span>
                        <span class="legend-label">{{ t.label }}</span>
                    </div>
                </div>

                <div class="months-grid">
                    <div class="month-card" v-for="m in 12" :key="m">
                        <div class="month-header">{{ monthName(m - 1) }} / {{ periodoYear }}</div>
                        <div class="dow-row">
                            <div class="dow">SEG</div>
                            <div class="dow">TER</div>
                            <div class="dow">QUA</div>
                            <div class="dow">QUI</div>
                            <div class="dow">SEX</div>
                            <div class="dow">SÁB</div>
                            <div class="dow">DOM</div>
                        </div>
                        <div class="days-grid">
                            <div
                                v-for="(cell, idx) in monthCells(m - 1)"
                                :key="idx"
                                class="day-cell"
                                :class="cellClass(cell)"
                                :style="cellStyle(cell)"
                                @click="onCellClick(cell)"
                            >
                                <div class="day-number">{{ cell.day || '' }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <el-dialog title="Gerenciar datas" :visible.sync="dialogManage" width="520px">
                <div>
                    <div style="margin-bottom: 8px;">
                        <b>Datas selecionadas:</b> {{ selectedDates.length }}
                    </div>
                    <div class="selected-preview">
                        <span class="selected-pill" v-for="d in selectedDatesPreview" :key="d">{{ formatDateBR(d) }}</span>
                        <span v-if="selectedDates.length > selectedDatesPreview.length" class="selected-more">
                            +{{ selectedDates.length - selectedDatesPreview.length }}
                        </span>
                    </div>
                </div>

                <el-form label-position="top" class="u-mt-10">
                    <el-form-item label="Marcação">
                        <el-select v-model="selectedTipo" placeholder="Selecione">
                            <el-option v-for="(t, i) in tipos" :key="i" :label="t.label" :value="t.key"></el-option>
                            <el-option label="(Limpar marcação)" value=""></el-option>
                        </el-select>
                    </el-form-item>
                </el-form>

                <div slot="footer" class="dialog-footer">
                    <el-button @click="dialogManage = false">CANCELAR</el-button>
                    <el-button type="primary" :disabled="isSMED && lockedGlobal" :loading="saving" @click="applyManage()">
                        APLICAR
                    </el-button>
                </div>
            </el-dialog>
        </c-panel>
    </div>
</template>

<script>
import cPanel from '../../shared/c-panel/c-panel.vue'
import moment from 'moment'

export default {
    components: { cPanel },
    data: () => ({
        loading: false,
        saving: false,
        periodoId: '',
        lockedGlobal: false,
        tipos: [],
        marks: {},
        selectedDates: [],
        dialogManage: false,
        selectedTipo: 'dia_letivo'
    }),
    computed: {
        periodosList () {
            const p = this.$store && this.$store.state ? this.$store.state.periodos : []
            if (Array.isArray(p)) return p
            if (p && typeof p === 'object') return Object.values(p)
            return []
        },
        isSMED () {
            const t = Number(this.$store.state.user.tipo)
            return [4, 5, 6].includes(t)
        },
        instituicaoId () {
            const id = this.$store.state.config && this.$store.state.config.instituicao ? this.$store.state.config.instituicao.id : ''
            return id || ''
        },
        periodoYear () {
            const p = (this.periodosList || []).find(x => String(x.id) === String(this.periodoId))
            const nome = p && p.nome ? String(p.nome) : ''
            const year = parseInt(nome, 10)
            return Number.isFinite(year) ? year : null
        },
        metrics () {
            const year = this.periodoYear
            if (!year) return { qtdDiasLetivos: 0, diasLetivosPercorridos: 0, semanasLetivasPercorridas: 0 }

            const today = moment().startOf('day')
            const keys = Object.keys(this.marks || {}).filter(d => d && d.startsWith(year + '-'))

            const diasLetivos = keys.filter(d => this.marks[d] === 'dia_letivo')
            const percorridos = diasLetivos.filter(d => moment(d, 'YYYY-MM-DD').isSameOrBefore(today))

            const weeks = {}
            percorridos.forEach(d => {
                const md = moment(d, 'YYYY-MM-DD')
                const wk = md.isoWeek()
                const wy = md.isoWeekYear()
                weeks[`${wy}-${wk}`] = true
            })

            return {
                qtdDiasLetivos: diasLetivos.length,
                diasLetivosPercorridos: percorridos.length,
                semanasLetivasPercorridas: Object.keys(weeks).length
            }
        },
        selectedDatesPreview () {
            return this.selectedDates.slice(0, 10)
        }
    },
    methods: {
        monthName (m) {
            const names = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
            return names[m] || ''
        },
        formatDateBR (d) {
            if (!d) return '-'
            return moment(d, 'YYYY-MM-DD').format('DD/MM/YYYY')
        },
        pad2 (n) {
            return String(n).padStart(2, '0')
        },
        toDateStr (year, monthIdx, day) {
            return `${year}-${this.pad2(monthIdx + 1)}-${this.pad2(day)}`
        },
        monthCells (monthIdx) {
            const year = this.periodoYear
            if (!year) return []

            const first = new Date(year, monthIdx, 1)
            const jsDow = first.getDay() // 0=dom
            const start = (jsDow + 6) % 7 // 0=seg
            const daysInMonth = new Date(year, monthIdx + 1, 0).getDate()

            const cells = []
            for (let i = 0; i < start; i++) cells.push({ day: null, date: null })
            for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, date: this.toDateStr(year, monthIdx, d) })

            // completa até múltiplo de 7
            while (cells.length % 7 !== 0) cells.push({ day: null, date: null })
            return cells
        },
        isSelected (dateStr) {
            if (!dateStr) return false
            return this.selectedDates.includes(dateStr)
        },
        cellClass (cell) {
            if (!cell || !cell.date) return 'is-empty'
            const selected = this.isSelected(cell.date)
            const hasMark = !!(this.marks && this.marks[cell.date])
            return {
                'is-selected': selected,
                'has-mark': hasMark
            }
        },
        cellStyle (cell) {
            if (!cell || !cell.date) return {}
            const tipo = this.marks ? this.marks[cell.date] : null
            const cfg = (this.tipos || []).find(t => t.key === tipo)
            const style = {}
            if (cfg && cfg.color) style.backgroundColor = cfg.color
            return style
        },
        onCellClick (cell) {
            if (!cell || !cell.date) return
            const d = cell.date
            const idx = this.selectedDates.indexOf(d)
            if (idx >= 0) {
                this.selectedDates.splice(idx, 1)
            } else {
                this.selectedDates.push(d)
            }
        },
        openManage () {
            if (this.selectedDates.length < 1) return
            this.dialogManage = true
        },
        clearSelection () {
            this.selectedDates = []
        },
        async load () {
            if (!this.periodoId) return

            // Para usuários de instituição, se não houver instituição setada, mantém a tela em modo leitura (sem salvar).
            if (!this.isSMED && !this.instituicaoId) {
                window.eventBus.$emit('alert', { msg: 'Selecione uma instituição no sistema para personalizar o calendário.', type: 'warning' })
            }

            this.loading = true
            try {
                const url = this.isSMED || !this.instituicaoId
                    ? `${this.$store.state.config.baseUrlApi}/api/calendario-escolar/getByPeriodo/${this.periodoId}`
                    : `${this.$store.state.config.baseUrlApi}/api/calendario-escolar/getByPeriodo/${this.periodoId}/${this.instituicaoId}`

                const res = await this.$axios.get(url, this.$store.getters.getHeaderAuth)

                this.lockedGlobal = !!(res.data && res.data.locked_global)
                this.tipos = (res.data && res.data.tipos) ? res.data.tipos : []
                this.marks = (res.data && res.data.marks) ? res.data.marks : {}
            } catch (error) {
                this.$store.commit('throwException', error)
            } finally {
                this.loading = false
            }
        },
        async applyManage () {
            if (!this.periodoId) return
            if (this.selectedDates.length < 1) return
            if (this.isSMED && this.lockedGlobal) return
            if (!this.isSMED && !this.instituicaoId) return

            this.saving = true
            window.eventBus.$emit('loading', true)
            try {
                const payload = { datas: this.selectedDates, tipo: this.selectedTipo }
                if (this.isSMED) {
                    await this.$axios.post(
                        `${this.$store.state.config.baseUrlApi}/api/calendario-escolar/storeBatchBase/${this.periodoId}`,
                        payload,
                        this.$store.getters.getHeaderAuth
                    )
                } else {
                    await this.$axios.post(
                        `${this.$store.state.config.baseUrlApi}/api/calendario-escolar/storeBatchInstituicao/${this.periodoId}/${this.instituicaoId}`,
                        payload,
                        this.$store.getters.getHeaderAuth
                    )
                }

                this.dialogManage = false
                this.clearSelection()
                await this.load()
                window.eventBus.$emit('alert', { msg: 'Calendário atualizado com sucesso.', type: 'success' })
            } catch (error) {
                this.$store.commit('throwException', error)
            } finally {
                window.eventBus.$emit('loading', false)
                this.saving = false
            }
        },
        onPeriodoChange () {
            this.clearSelection()
            this.load()
        },
        printCalendario () {
            if (!this.periodoId) return

            if (!this.isSMED && !this.instituicaoId) {
                window.eventBus.$emit('alert', { msg: 'Selecione uma instituição no sistema para imprimir o calendário.', type: 'warning' })
                return
            }

            const param = { periodo_id: this.periodoId }
            if (!this.isSMED) {
                param.instituicao_id = this.instituicaoId
            }

            window.eventBus.$emit('print', { url: '/api/pdf/renderCalendarioEscolar', param })
        }
    },
    mounted () {
        this.periodoId = (this.$store.state.config && this.$store.state.config.periodo) ? this.$store.state.config.periodo.id : ''
        if (!this.periodoId && this.periodosList.length > 0) {
            // fallback: pega o último período (mais recente) quando possível
            const sorted = [...this.periodosList].sort((a, b) => Number(a.id) - Number(b.id))
            this.periodoId = sorted[sorted.length - 1].id
        }
        this.load()
    }
}
</script>

<style scoped>
.metric-box {
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 10px;
    background: #fafafa;
}
.metric-label {
    font-size: 12px;
    color: #666;
    text-transform: uppercase;
}
.metric-value {
    font-size: 22px;
    font-weight: 700;
    margin-top: 4px;
}

.legend {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin: 10px 0 15px 0;
}
.legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
}
.legend-color {
    width: 14px;
    height: 14px;
    border-radius: 3px;
    border: 1px solid rgba(0,0,0,.2);
}
.legend-label {
    color: #333;
}

.months-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
}
@media (max-width: 1100px) {
    .months-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 700px) {
    .months-grid { grid-template-columns: 1fr; }
}

.month-card {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    overflow: hidden;
    background: #fff;
}
.month-header {
    padding: 8px 10px;
    background: #f5f5f5;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 12px;
    border-bottom: 1px solid #e0e0e0;
}
.dow-row {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    background: #fafafa;
    border-bottom: 1px solid #eee;
}
.dow {
    font-size: 10px;
    font-weight: 700;
    text-align: center;
    padding: 6px 0;
    color: #555;
}
.days-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
}
.day-cell {
    height: 34px;
    border-right: 1px solid #eee;
    border-bottom: 1px solid #eee;
    cursor: pointer;
    position: relative;
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
    padding: 4px 6px;
    box-sizing: border-box;
    background: #fff;
    transition: filter .08s ease-in-out;
}
.day-cell:hover {
    filter: brightness(0.96);
}
.day-cell.is-empty {
    cursor: default;
    background: #fbfbfb;
}
.day-number {
    font-size: 11px;
    font-weight: 700;
    color: rgba(0,0,0,.75);
}
.day-cell.has-mark .day-number {
    color: #fff;
}
.day-cell.is-selected {
    /* outline: 3px solid #1976d2; */
    z-index: 2;
    background-color: #e7e7e7;
}
.selected-preview {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
}
.selected-pill {
    font-size: 11px;
    padding: 3px 8px;
    border-radius: 12px;
    background: #f0f0f0;
    border: 1px solid #e0e0e0;
}
.selected-more {
    font-size: 11px;
    color: #666;
    align-self: center;
}
</style>

